import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useContext, useEffect } from 'react';
import { toast } from 'react-toastify';
import { IconLoader2 } from '@tabler/icons-react';
import { WarningMessage } from '@common/components';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '../ui/input';
import { LangContext } from '@/context/LangProvider';
import { Button } from '../ui/button';
import { useMutateUnpublishProject } from '@/reactQuery/post';
import { useGetConversationCounts } from '@/reactQuery/get';
import { showToastErrorMessage } from '@/utils/showToastErrorMessage';

interface UnpublishProjectModalProps {
  projectId: string;
  projectTitle: string;
  organizationId?: string;
  workspaceId?: string;
  isOpen: boolean;
  onClose: () => void;
}

interface UnpublishFormData {
  name: string;
  conversationAction: 'keep' | 'delete';
}

const defaultValues: UnpublishFormData = {
  name: '',
  conversationAction: 'keep',
};

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Project name is required'),
  conversationAction: Yup.string().oneOf(['keep', 'delete']).required(),
});

function UnpublishProjectModal({
  projectId,
  projectTitle,
  organizationId,
  workspaceId,
  isOpen,
  onClose,
}: UnpublishProjectModalProps) {
  const { lang } = useContext(LangContext);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<UnpublishFormData>({
    defaultValues,
    resolver: yupResolver(validationSchema),
  });

  const {
    data: conversationCounts,
    isLoading: isLoadingCounts,
    refetch: refetchCounts,
  } = useGetConversationCounts(organizationId, workspaceId, projectId, isOpen);

  const { mutateAsync: mutateAsyncUnpublish } = useMutateUnpublishProject(
    organizationId,
    workspaceId,
    projectId,
  );

  const conversationAction = watch('conversationAction');
  const hasOngoingConversations = (conversationCounts?.active ?? 0) > 0;
  const hasCompletedConversations = (conversationCounts?.completed ?? 0) > 0;

  useEffect(() => {
    if (isOpen) {
      reset(defaultValues);
      refetchCounts();
    }
  }, [isOpen, reset, refetchCounts]);

  // Titles can carry invisible whitespace the user cannot reproduce: French
  // titles pasted from Word contain a narrow no-break space (U+202F) before
  // "?", and untrimmed titles keep trailing/double spaces that HTML rendering
  // hides. Compare on a normalized form so the visibly identical name matches.
  const normalizeTitle = (value: string) => value.normalize('NFC').replace(/\s+/g, ' ').trim();

  const onSubmit: SubmitHandler<UnpublishFormData> = async (data) => {
    if (normalizeTitle(data.name) !== normalizeTitle(projectTitle)) {
      toast.error(lang.get('msg.invalidProjectName'));
      return;
    }

    if (hasOngoingConversations) {
      toast.error(lang.get('msg.cannotUnpublishOngoingConversations'));
      return;
    }

    try {
      await mutateAsyncUnpublish({
        deleteCompletedConversations: data.conversationAction === 'delete',
      });
      toast.success(lang.get('msg.projectSuccessfullyUnpublished'));
      onClose();
    } catch (error) {
      showToastErrorMessage(error);
    }
  };

  const handleClose = () => {
    reset(defaultValues);
    onClose();
  };

  return (
    <AlertDialog open={isOpen}>
      <AlertDialogContent className="max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit(onSubmit)}>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-1">
              {lang.get('msg.unpublishProject')}
            </AlertDialogTitle>
            <p className="text-sm text-dark-text">
              {lang.get('msg.areYouSureYouWantToUnpublishProject')}
              <span className="font-bold"> {projectTitle}</span>?
            </p>
            <p className="text-sm text-amber-700 bg-amber-50 p-3 rounded-lg mt-3">
              {lang.get('msg.unpublishRecruitmentNote')}
            </p>
          </AlertDialogHeader>

          {isLoadingCounts ? (
            <div className="flex items-center justify-center py-8">
              <IconLoader2 className="animate-spin text-primary" size={24} />
            </div>
          ) : (
            <>
              {/* Error message if ongoing conversations exist */}
              {hasOngoingConversations && (
                <div className="p-3 mt-4 text-sm rounded-lg bg-red-50 text-red-700">
                  <div className="flex items-start gap-2">
                    <span className="text-red-600">⚠</span>
                    {lang.get('msg.cannotUnpublishOngoingConversationsWarning', {
                      count: String(conversationCounts?.active ?? 0),
                    })}
                  </div>
                </div>
              )}

              {/* Conversation counts display */}
              <div className="py-4 space-y-2">
                <p className="text-sm text-dark-text">
                  {lang.get('msg.ongoingConversations')}:{' '}
                  <strong>{conversationCounts?.active ?? 0}</strong>
                </p>
                <p className="text-sm text-dark-text">
                  {lang.get('msg.completedConversations')}:{' '}
                  <strong>{conversationCounts?.completed ?? 0}</strong>
                </p>
              </div>

              {/* Radio options for completed conversations */}
              {!hasOngoingConversations && hasCompletedConversations && (
                <div className="py-4 space-y-3 border-t border-b border-gray-200">
                  <p className="text-sm font-medium text-dark-text">
                    {lang.get('msg.whatToDoWithCompletedConversations')}
                  </p>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        {...register('conversationAction')}
                        value="keep"
                        checked={conversationAction === 'keep'}
                        onChange={() => setValue('conversationAction', 'keep')}
                        className="w-4 h-4 cursor-pointer accent-primary"
                      />
                      <span className="text-sm text-dark-text">
                        {lang.get('msg.keepConversations')}
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        {...register('conversationAction')}
                        value="delete"
                        checked={conversationAction === 'delete'}
                        onChange={() => setValue('conversationAction', 'delete')}
                        className="w-4 h-4 cursor-pointer accent-primary"
                      />
                      <span className="text-sm text-dark-text">
                        {lang.get('msg.deleteConversations')}
                      </span>
                    </label>
                  </div>
                </div>
              )}

              {/* Warning message - different based on keep/delete selection */}
              {!hasOngoingConversations && (
                <div className="py-4">
                  <WarningMessage>
                    {hasCompletedConversations && conversationAction === 'delete'
                      ? lang.get('msg.unpublishDeleteAllWarning')
                      : lang.get('msg.unpublishAnalysisDataWarning')}
                  </WarningMessage>
                </div>
              )}

              {/* Name confirmation input */}
              {!hasOngoingConversations && (
                <div className="flex flex-col pb-6">
                  <label className="relative flex flex-col w-full gap-1">
                    <p className="mb-2 text-sm text-dark-text">
                      {lang.get('msg.toProceedPleaseEnterProjectName')}
                    </p>
                    <Input
                      {...register('name')}
                      placeholder={lang.get('msg.enterProjectName')}
                      className={`w-full ${errors?.name?.message && 'border-red-600/50'}`}
                      disabled={hasOngoingConversations}
                    />
                    {errors?.name?.message && (
                      <p className="text-xs font-medium text-red-600 mt-1">{errors.name.message}</p>
                    )}
                  </label>
                </div>
              )}
            </>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleClose}>{lang.get('msg.cancel')}</AlertDialogCancel>
            <Button
              type="submit"
              className="bg-orange-600 hover:bg-orange-700"
              disabled={hasOngoingConversations || isSubmitting || isLoadingCounts}
            >
              {isSubmitting ? (
                <span className="flex items-center gap-1.5">
                  <IconLoader2 className="animate-spin" size={18} />
                  {lang.get('msg.unpublishing')}...
                </span>
              ) : (
                lang.get('msg.unpublish')
              )}
            </Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default UnpublishProjectModal;
